import React, { useEffect, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import { MappedDatePicker } from "@ui/components/DatePicker";
import * as Yup from "yup";
import Datagrid from "@ui/components/DataGrid";
import { TableSortLabel } from "@material-ui/core";
import { sortHeaderStyle } from "../../../constants";
import { renderCommonTestsRow } from "../helpers";
import Button from "../../../../ui/components/Button";
import { MappedInput } from "../../../../ui/components/Input";

const SCREENS_HEADINGS = [
  ["code", "Test Code"],
  ["name", "Test Name"],
];

const COMMODITIES_HEADINGS = [["name", "Commodity Name"]];
const STATES_HEADINGS = [["name", "States"]];

const headerStyle = {
  background: "#E8F5E1",
  color: "#71BF44",
};

const validationSchema = Yup.object({
  start_date: Yup.date()
    .nullable()
    .required("Start date is required"),
  end_date: Yup.date()
    .nullable()
    // .required("End date is required")
    .min(Yup.ref("start_date"), "End date must be after start date"),
  selectedScreens: Yup.object().test(
    "atLeastOneScreen",
    "Select at least one test",
    (value) => Object.values(value).some(Boolean)
  ),
  selectedCommodities: Yup.object().test(
    "atLeastOneCommodity",
    "Select at least one commodity",
    (value) => Object.values(value).some(Boolean)
  ),
});

const ReportDateRange = () => {
  const { values, setFieldValue } = useFormikContext();
  const today = new Date();

  useEffect(() => {
    if (values.start_date && values.end_date) {
      const startDate = new Date(values.start_date);
      const endDate = new Date(values.end_date);

      if (endDate <= startDate) {
        setFieldValue("end_date", null);
      }
    }
  }, [values.start_date, values.end_date, setFieldValue]);

  return (
    <div className="row mb-5">
      <div className="col-6">
        <MappedDatePicker
          name="start_date"
          label="Start Date"
          testId="start_date"
          maxDate={today}
        />
      </div>
      <div className="col-6">
        <MappedDatePicker
          name="end_date"
          label="End Date"
          testId="end_date"
          minDate={values.start_date ? new Date(values.start_date) : null}
          maxDate={today}
          disabled={!values.start_date}
        />
      </div>
    </div>
  );
};

const CommoditySelection = ({ commodities, loading }) => {
  const { values, setFieldValue } = useFormikContext();
  const [direction, setDirection] = useState("desc");
  const [sortedData, setSortedData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    if (commodities && Array.isArray(commodities)) {
      setSortedData([...commodities]);
      setFilteredData([...commodities]);
    }
  }, [commodities]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(sortedData);
      return;
    }

    const filtered = sortedData.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, sortedData]);

  const selectedCommoditiesCount = Object.values(
    values.selectedCommodities || {}
  ).filter(Boolean).length;

  const handleSort = () => {
    const newDirection = direction === "desc" ? "asc" : "desc";
    setDirection(newDirection);

    const sorted = [...sortedData].sort(
      newDirection === "desc"
        ? (a, b) => a.name.localeCompare(b.name)
        : (a, b) => b.name.localeCompare(a.name)
    );

    setSortedData(sorted);

    const filteredSorted = [...filteredData].sort(
      newDirection === "desc"
        ? (a, b) => a.name.localeCompare(b.name)
        : (a, b) => b.name.localeCompare(a.name)
    );

    setFilteredData(filteredSorted);
  };

  function renderHeaderWithSorting(headings) {
    const safeHeadings = Array.isArray(headings) ? headings : [];

    return safeHeadings.map(([key, header]) => (
      <th
        key={key}
        style={sortHeaderStyle}
        className="text-nowrap"
        onClick={() => key === "name" && handleSort()}
      >
        <span style={{ cursor: "pointer" }}>{header}</span>
        {key === "name" && <TableSortLabel active direction={direction} />}
      </th>
    ));
  }

  const handleCommoditySelection = (selectedItems) => {
    setFieldValue("selectedCommodities", selectedItems);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="d-flex flex-column col-3 p-0">
      <h6 className="mb-3">Select Commodities</h6>
      <div className="border 1px solid #E5E5E5 h-100">
        <div
          className="d-flex justify-content-between p-5 align-items-center"
          style={headerStyle}
        >
          <div className="d-flex align-items-center justify-content-between w-100">
            <h6 className="mb-0 mr-3">{`${
              selectedCommoditiesCount > 1
                ? `${selectedCommoditiesCount} items`
                : `${selectedCommoditiesCount} item`
            } selected`}</h6>
            <MappedInput
              name="commoditySearch"
              placeholder="Search commodities..."
              value={searchTerm}
              onChange={handleSearchChange}
              variant="naked"
              color="#71BF44"
              icon="search"
              iconPosition="start"
              style={{ width: "160px" }}
              formgroupstyle={{ marginBottom: 0 }}
            />
          </div>
        </div>
        <Datagrid
          data={filteredData}
          headings={COMMODITIES_HEADINGS}
          renderHeaderWithSorting={renderHeaderWithSorting}
          renderRow={(headings, item) => renderCommonTestsRow(headings, item)}
          hideSelect={false}
          selected={values.selectedCommodities}
          setSelected={handleCommoditySelection}
          selectable={true}
          headerClassName="nospaces-header"
          loading={loading}
          contentMinWidth="100%"
        />
      </div>
    </div>
  );
};

const ScreenSelection = ({ screens, loading }) => {
  const { values, setFieldValue } = useFormikContext();
  const [direction, setDirection] = useState("desc");
  const [sortedData, setSortedData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    if (screens && Array.isArray(screens)) {
      setSortedData([...screens]);
      setFilteredData([...screens]);
    }
  }, [screens]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(sortedData);
      return;
    }

    const filtered = sortedData.filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, sortedData]);

  const selectedScreensCount = Object.values(
    values.selectedScreens || {}
  ).filter(Boolean).length;

  const ascSort = (a, b) => {
    if (a.code > b.code) return 1;
    if (a.code < b.code) return -1;
    return 0;
  };

  const descSort = (a, b) => {
    if (a.code > b.code) return -1;
    if (a.code < b.code) return 1;
    return 0;
  };

  const handleSort = () => {
    const newDirection = direction === "desc" ? "asc" : "desc";
    setDirection(newDirection);

    const sorted = [...sortedData].sort(
      newDirection === "desc" ? ascSort : descSort
    );
    setSortedData(sorted);

    const filteredSorted = [...filteredData].sort(
      newDirection === "desc" ? ascSort : descSort
    );

    setFilteredData(filteredSorted);
  };

  function renderHeaderWithSorting(headings) {
    return headings.map(([key, header]) => (
      <th
        key={key}
        style={{ ...sortHeaderStyle, minWidth: 120 }}
        className="text-nowrap"
        onClick={() => handleSort()}
      >
        <span style={{ cursor: "pointer" }}>{header}</span>
        {<TableSortLabel active direction={direction} />}
      </th>
    ));
  }

  const handleScreenSelection = (selectedItems) => {
    setFieldValue("selectedScreens", selectedItems);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="d-flex flex-column col-5 p-0 mx-auto">
      <h6 className="mb-3">Select Screens</h6>

      <div className="border 1px solid #E5E5E5">
        <div
          className="d-flex justify-content-between p-5 align-items-center"
          style={headerStyle}
        >
          <div className="d-flex align-items-center justify-content-between w-100">
            <h6 className="mb-0 mr-3">{`${
              selectedScreensCount > 1
                ? `${selectedScreensCount} items`
                : `${selectedScreensCount} item`
            } selected`}</h6>
            <MappedInput
              name="screenSearch"
              placeholder="Search tests..."
              value={searchTerm}
              onChange={handleSearchChange}
              variant="naked"
              color="#71BF44"
              icon="search"
              iconPosition="start"
              style={{ width: "160px" }}
              formgroupstyle={{ marginBottom: 0 }}
            />
          </div>
        </div>
        <Datagrid
          data={filteredData}
          headings={SCREENS_HEADINGS}
          renderHeaderWithSorting={renderHeaderWithSorting}
          renderRow={renderCommonTestsRow}
          hideSelect={false}
          selected={values.selectedScreens}
          setSelected={handleScreenSelection}
          selectable={true}
          headerClassName="nospaces-header"
          loading={loading}
          contentMinWidth="100%"
        />
      </div>
    </div>
  );
};

const StatesSelection = ({ states, loading }) => {
  const { values, setFieldValue } = useFormikContext();
  const [direction, setDirection] = useState("desc");
  const [sortedData, setSortedData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    if (states && Array.isArray(states)) {
      setSortedData([...states]);
      setFilteredData([...states]);
    }
  }, [states]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(sortedData);
      return;
    }

    const filtered = sortedData.filter((item) =>
      item.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, sortedData]);

  const selectedStatesCount = Object.values(values.selectedStates || {}).filter(
    Boolean
  ).length;

  const ascSort = (a, b) => {
    if (a.label > b.label) return 1;
    if (a.label < b.label) return -1;
    return 0;
  };

  const descSort = (a, b) => {
    if (a.label > b.label) return -1;
    if (a.label < b.label) return 1;
    return 0;
  };

  const handleSort = () => {
    const newDirection = direction === "desc" ? "asc" : "desc";
    setDirection(newDirection);

    const sorted = [...sortedData].sort(
      newDirection === "desc" ? ascSort : descSort
    );
    setSortedData(sorted);

    const filteredSorted = [...filteredData].sort(
      newDirection === "desc" ? ascSort : descSort
    );

    setFilteredData(filteredSorted);
  };

  function renderHeaderWithSorting(headings) {
    return headings.map(([key, header]) => (
      <th
        key={key}
        style={{ ...sortHeaderStyle, minWidth: 120 }}
        className="text-nowrap"
        onClick={() => handleSort()}
      >
        <span style={{ cursor: "pointer" }}>{header}</span>
        {<TableSortLabel active direction={direction} />}
      </th>
    ));
  }

  const handleStateSelection = (selectedItems) => {
    setFieldValue("selectedStates", selectedItems);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="d-flex flex-column col-3 p-0">
      <h6 className="mb-3">Select States</h6>

      <div className="border 1px solid #E5E5E5">
        <div
          className="d-flex justify-content-between p-5 align-items-center"
          style={headerStyle}
        >
          <div className="d-flex align-items-center justify-content-between w-100">
            <h6 className="mb-0 mr-3">{`${
              selectedStatesCount > 1
                ? `${selectedStatesCount} items`
                : `${selectedStatesCount} item`
            } selected`}</h6>
            <MappedInput
              name="statesSearch"
              placeholder="Search states..."
              value={searchTerm}
              onChange={handleSearchChange}
              variant="naked"
              color="#71BF44"
              icon="search"
              iconPosition="start"
              style={{ width: "160px" }}
              formgroupstyle={{ marginBottom: 0 }}
            />
          </div>
        </div>
        <Datagrid
          data={filteredData}
          headings={STATES_HEADINGS}
          renderHeaderWithSorting={renderHeaderWithSorting}
          renderRow={(headings, item) => renderCommonTestsRow(headings, item)}
          hideSelect={false}
          selected={values.selectedStates}
          setSelected={handleStateSelection}
          selectable={true}
          headerClassName="nospaces-header"
          loading={loading}
          contentMinWidth="100%"
        />
      </div>
    </div>
  );
};

const SalesReportStep1 = ({
  onSubmit,
  screens,
  commodities,
  states,
  loading,
  initialValues,
}) => {
  // Function to get default dates - start date as 1 year ago, end date as today
  const getDefaultDates = () => {
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);

    return {
      startDate: oneYearAgo,
      endDate: today,
    };
  };

  const getInitialScreenSelections = () => {
    const initialSelections = {};
    if (screens && Array.isArray(screens)) {
      screens.forEach((screen) => {
        initialSelections[screen.id] = true;
      });
    }
    return initialSelections;
  };

  const getInitialCommoditySelections = () => {
    const initialSelections = {};
    if (commodities && Array.isArray(commodities)) {
      commodities.forEach((commodity) => {
        initialSelections[commodity.id] = true;
      });
    }
    return initialSelections;
  };

  const getInitialStatesSelections = () => {
    const initialSelections = {};
    if (states && Array.isArray(states)) {
      states.forEach((states) => {
        initialSelections[states.id] = true;
      });
    }
    return initialSelections;
  };
  const defaultDates = getDefaultDates();

  const formInitialValues = initialValues || {
    start_date: defaultDates.startDate,
    end_date: defaultDates.endDate,
    selectedScreens: getInitialScreenSelections(),
    selectedCommodities: getInitialCommoditySelections(),
    selectedStates: getInitialStatesSelections(),
  };

  const handleSubmit = (values) => {
    onSubmit(values);
  };

  return (
    <>
      <h4>
        <strong>Data Sales Report</strong>
      </h4>
      <hr />

      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, isSubmitting, isValid }) => (
          <Form>
            <div className="mb-4 col-5 p-0">
              <h6 className="mb-3">Select Date Range</h6>
              <ReportDateRange />
              {errors.start_date && touched.start_date && (
                <div className="text-danger">{errors.start_date}</div>
              )}
              {errors.end_date && touched.end_date && (
                <div className="text-danger">{errors.end_date}</div>
              )}
            </div>

            <div className="my-10">
              <div className="d-flex justify-content-between">
                <CommoditySelection
                  commodities={commodities}
                  loading={loading}
                />
                <ScreenSelection screens={screens} loading={loading} />
                <StatesSelection states={states} loading={loading} />
              </div>
              <div className="d-flex justify-content-between">
                <div
                  className={`text-danger mt-0 rounded-bottom col-5 ${errors.selectedCommodities &&
                    "bg-warning text-white"}`}
                >
                  {errors.selectedCommodities && errors.selectedCommodities}
                </div>
                <div
                  className={`text-danger mt-0 rounded-bottom col-6 mx-auto ${errors.selectedScreens &&
                    "bg-warning text-white"}`}
                >
                  {errors.selectedScreens && errors.selectedScreens}
                </div>
              </div>
            </div>

            <div className="text-right mt-8">
              <Button
                type="submit"
                label="Continue"
                // size="small"
                text="#FFFFFF"
                background="#71BF44"
                boxShadow
                disabled={isSubmitting || !isValid}
                className="text-uppercase"
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default SalesReportStep1;
